<template>
  <b-list-group-item
    bg-variant="light" class="px-2 py-2"
    :button="item.children && item.children.length > 0"
    @click.stop="visible = !visible">
    <div class="d-flex justify-content-between align-items-center">

      <div class="d-inline-flex align-items-center">
        <div v-if="item.children && item.children.length" class="mx-2">
          <span class="float-right" v-if="visible">
            <font-awesome-icon  icon="chevron-up" size="xs" />
          </span>
          <span class="float-right" v-if="!visible">
            <font-awesome-icon icon="chevron-right" size="xs" />
          </span>
        </div>

        {{ item.name }}
      </div>

      <span @click.stop>
        <b-form-checkbox
          v-model="item.checked"
          class="ml-1 custom-checkbox-yellow"
          :indeterminate="item.indeterminate"
          :disabled="isCheckboxDisabled(item.code) || isDisabled"
          @change="handleCheckboxChange(item)"
        />
      </span>

    </div>

    <b-collapse
      :id="'collapse-'+item.code" class="mt-2" v-model="visible"
      v-if="item.children && item.children.length > 0"
    >
      <b-list-group class="perm-group" bg-variant="light">
        <PermissionListItem v-for="(child, index) in item.children" :key="index" :item="child" :tree-data="treeData" :is-disabled="isDisabled" :role-code="roleCode" />
      </b-list-group>
    </b-collapse>

  </b-list-group-item>
</template>

<script>
export default {
  name: "PermissionListItem",
  props: {
    item: { type: Object, required: true },
    treeData: { type: Array, required: true },
    roleCode: { type: String, default: ''},
    isDisabled: { type: Boolean, default: false }
  },
  data() {
    return {
      visible: false
    }
  },
  methods: {
    handleClick(event) {
      console.log('Клик по флажку!');
      // Останавливаем всплытие события click
      event.stopPropagation();
    },
    // Обработчик изменения чекбокса
    handleCheckboxChange(node) {
      const isChecked = node.checked;

      // Обновляем READ, если выбран EDIT
      // TODO: закопаться и посмотреть как на фронте проверяются права. e.g. Скрывать блоки в сайдбаре если у юзера нет нужных прав.
      if (node.code.endsWith('_EDIT') && isChecked) {
        const readCode = node.code.replace('_EDIT', '_READ');
        const readNode = this.findNodeByCode(readCode);

        if (readNode && !readNode.checked) {
          readNode.checked = true;
          this.updateParents(readNode.parent_code);
        }
      }

      // Оновляем EDIT_OTHER, если не выбран EDIT
      if (node.code.endsWith('_EDIT') && !isChecked) {
        const otherCode = node.code.replace('_EDIT', '_EDIT_OTHER');
        const otherNode = this.findNodeByCode(otherCode);

        if (otherNode && otherNode.checked) {
          otherNode.checked = false;
          this.updateParents(otherNode.parent_code);
        }
      }

      // Обновляем состояние текущего узла
      this.updateNodeState(node.code, isChecked, false);

      // Обновляем всех потомков
      this.updateChildren(node, isChecked);

      // Обновляем всех родителей
      this.updateParents(node.parent_code);
    },
    isCheckboxDisabled(nodeCode) {
      const disabledCode = ['ROLE', 'ROLE_READ', 'ROLE_EDIT'];

      if (this.roleCode === 'admin' && disabledCode.includes(nodeCode)) {
        return true;
      }

      if (nodeCode.endsWith('_READ')) {
        const editCode = nodeCode.replace('_READ', '_EDIT');
        const editNode = this.findNodeByCode(editCode);

        return editNode ? editNode.checked : false;
      }

      if (nodeCode.endsWith('_EDIT_OTHER')) {
        const editCode = nodeCode.replace('_EDIT_OTHER', '_EDIT');
        const editNode = this.findNodeByCode(editCode);

        return editNode ? !editNode.checked : false;
      }
      return false;
    },
    // Обновление состояния узла
    updateNodeState(nodeCode, checked, indeterminate) {
      const node = this.findNodeByCode(nodeCode);
      if (node) {
        node.checked = checked;
        node.indeterminate = indeterminate;
      }
    },
    // Поиск узла по коду
    findNodeByCode(code) {
      const findRecursive = (nodes) => {
        for (const node of nodes) {
          if (node.code === code) {
            return node;
          }
          if (node.children && node.children.length > 0) {
            const found = findRecursive(node.children);
            if (found) return found;
          }
        }
        return null;
      };
      return findRecursive(this.treeData);
    },
    // Обновление всех потомков
    updateChildren(parentNode, isChecked) {
      const updateRecursive = (node) => {
        node.checked = isChecked;
        node.indeterminate = false;

        if (node.children && node.children.length > 0) {
          node.children.forEach(child => updateRecursive(child));
        }
      };

      // Начинаем с детей текущего узла
      if (parentNode.children) {
        parentNode.children.forEach(child => updateRecursive(child));
      }
    },
    // Обновление всех родителей
    updateParents(parentCode) {
      if (!parentCode) return;

      const parentNode = this.findNodeByCode(parentCode);
      if (!parentNode) return;

      this.updateParentState(parentNode);

      // Рекурсивно обновляем вышестоящих родителей
      if (parentNode.parent_code) {
        this.updateParents(parentNode.parent_code);
      }
    },
    // Обновление состояния родительского узла
    updateParentState(parentNode) {
      if (!parentNode.children || parentNode.children.length === 0) {
        parentNode.indeterminate = false;
        return;
      }

      // Получаем все дочерние узлы
      const allChildren = this.getAllChildren(parentNode);

      // Проверяем состояние детей
      const allChecked = allChildren.every(child => child.checked);
      const someChecked = allChildren.some(child => child.checked || child.indeterminate);

      // Обновляем состояние родителя
      parentNode.checked = allChecked;
      parentNode.indeterminate = !allChecked && someChecked;
    },
    // Получение всех дочерних узлов
    getAllChildren(parentNode) {
      const children = [];

      const collectChildren = (node) => {
        if (node.children && node.children.length > 0) {
          node.children.forEach(child => {
            children.push(child);
            collectChildren(child);
          });
        }
      };

      collectChildren(parentNode);
      return children;
    },
  }
}
</script>

<style>
.perm-group {
  padding-left: 5px;
}

.custom-checkbox-yellow .custom-control-input:indeterminate ~ .custom-control-label::after {
  background-color: #ffc107 !important;
  border-color: #ffc107 !important;
  width: 8px !important;
  height: 8px !important;
  left: -20px !important;
  top: 8px !important;
  border-radius: 1px;
}
</style>



// WEBPACK FOOTER //
// src/components/Users/PermissionListItem.vue